import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const AuriHelpModal = ({ isOpen, onClose, onCreateIdea }) => {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const messagesEndRef = useRef(null)
  const hasUserMessage = useRef(false)

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm Auri, and I'm here to help you get the most out of Opptora. Whether you have questions about your idea, want to understand what our specialists do, or need help navigating the platform — just ask!"
      }])
    }
  }, [isOpen, messages.length])

  // Only scroll after user interaction (more than 1 message)
  useEffect(() => {
    if (messages.length > 1) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  if (!isOpen) return null

  // Track when user sends a message
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return

    const userMessage = inputValue.trim()
    setInputValue('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setIsLoading(true)
    hasUserMessage.current = true

    try {
      const response = await apiService.auriHelp({ message: userMessage, history: messages })
      setMessages(prev => [...prev, { role: 'assistant', content: response.message }])

      if (response.action === 'open_new_idea' && onCreateIdea) {
        setTimeout(() => {
          handleClose()
          onCreateIdea()
        }, 1500)
      }
    } catch (err) {
      console.error('Auri help error:', err)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I had trouble processing that. Could you try asking again?"
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = async () => {
    // Save the conversation if user has submitted at least 1 message
    if (hasUserMessage.current && messages.length > 1) {
      setIsSaving(true)
      try {
        await apiService.saveHelpConversation(messages)
      } catch (err) {
        console.error('Failed to save help conversation:', err)
        // Don't block closing even if save fails
      } finally {
        setIsSaving(false)
      }
    }

    setMessages([])
    setInputValue('')
    setShowMobileNav(false)
    hasUserMessage.current = false
    onClose()
  }

  const basePath = import.meta.env.BASE_URL

  return (
    <>
      {/* Mobile Layout - Full screen with avatar in messages */}
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-900 sm:hidden">
        {/* Mobile Header Row 1 - Opptora branding + nav menu */}
        <div className="flex-shrink-0 bg-gray-800 border-b border-white/10 relative">
          <div className="flex h-12 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <img src={`${basePath}images/logo.png`} alt="Opptora" className="w-6 h-6" />
              <span className="text-white font-semibold text-base">Opptora</span>
            </div>
            {/* Nav menu button */}
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNav && (
            <div className="absolute top-full right-0 left-0 bg-gray-800 border-b border-white/10 shadow-lg z-50">
              <nav className="py-2">
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  Dashboard
                </a>
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  My Ideas
                </a>
                <button
                  onClick={handleClose}
                  disabled={isSaving}
                  className="w-full text-left px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50"
                >
                  Close Chat
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* Mobile Header Row 2 - Chat context + close */}
        <div className="flex-shrink-0 bg-gray-800/50 border-b border-white/10">
          <div className="flex h-11 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                  alt="Auri"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-1.5"
                />
              </div>
              <span className="text-white text-sm font-medium">Chatting with Auri</span>
            </div>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Messages - with avatar */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              {message.role === 'assistant' && (
                <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  <img
                    src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                    alt="Auri"
                    className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                  />
                </div>
              )}
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-100'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                  alt="Auri"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-gray-800">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Mobile Input */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-gray-800">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </form>
        </div>
      </div>

      {/* Desktop Layout - Modal with floating character */}
      <div className="hidden sm:block fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-start justify-center min-h-screen px-4 pb-20 text-center" style={{ paddingTop: '200px' }}>
          <div className="fixed inset-0 bg-gray-900/80 transition-opacity" onClick={isSaving ? undefined : handleClose} />

          <div className="relative inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-white/10">
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-64 h-64 z-50">
              <img
                src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                alt="Auri"
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>

            <button
              onClick={handleClose}
              disabled={isSaving}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>

            <div className="px-6 pt-20 pb-6">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-light text-white mb-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  How can I help?
                </h2>
                <p className="text-gray-400 text-sm">
                  Ask me anything about using Opptora
                </p>
              </div>

              <div className="h-64 overflow-y-auto mb-4 space-y-3 pr-2">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-xl ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-700 text-gray-200 px-4 py-2 rounded-xl">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask me anything..."
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

AuriHelpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateIdea: PropTypes.func
}

export default AuriHelpModal
